import Seo from "@/components/Seo";
import { FadeUp, Overline, GoldRule } from "@/components/Reveal";
import { TerraButton } from "@/components/Buttons";
import PayDepositButton from "@/components/PayDepositButton";
import { ASSETS, IMG, SITE } from "@/constants/site";

const OFFERINGS = [
  {
    n: "01",
    title: "Your event, our space",
    copy: "Host your gathering in the Sita space. A supper club of your own, a celebration, a women's table, an LGBTQIA night, a birthday that deserves more than a restaurant. You bring the people and the reason. We bring the room, the food, and the care.",
    img: IMG.privateSpace,
    leaf: ASSETS.leafSet1,
    testid: "private-events-space",
  },
  {
    n: "02",
    title: "In-home meal prep",
    copy: "Let us stock your week with food that actually nourishes you. We build a menu around your body, your tastes, and anything you cannot eat, then cook it fresh and leave it ready to go. Real meals, made from scratch, waiting for you all week.",
    img: IMG.privateMealPrep,
    leaf: ASSETS.leafSet1,
    testid: "private-events-meal-prep",
  },
  {
    n: "03",
    title: "Personal chef",
    copy: "For the nights you want to be a guest at your own table. We plan the menu, bring the groceries, cook in your home, and leave your kitchen cleaner than we found it. Dinner parties, date nights, quiet celebrations, all yours.",
    img: IMG.privateChef,
    leaf: ASSETS.leafSet1,
    testid: "private-events-personal-chef",
  },
];

export default function PrivateEvents() {
  return (
    <div>
      <Seo
        title="Private Events & Chef Services — Sita · Borrow Our Space or Bring Us Home"
        description="Host your night in the Sita space, book in-home meal prep, or bring on a personal chef. Sita handles the food and the care so you can be present with your people."
      />

      {/* ---------- Hero ---------- */}
      <section data-testid="private-events-hero" className="mx-auto max-w-7xl px-6 pb-14 pt-14 md:px-10 md:pb-20 md:pt-20">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <Overline>Private events &amp; chef services</Overline>
              <h1 className="mt-4 font-display text-5xl font-light leading-[1.05] text-forest md:text-6xl">
                Bring Sita<br />to your table
              </h1>
              <GoldRule className="mt-8" />
              <p className="mt-8 text-lg leading-relaxed text-ink/85">
                Sita is more than a monthly dinner. Borrow our space for your own night, or bring us into your home.
                However you want to gather, we handle the food and the care so you can be present with your people.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-6">
            <div className="overflow-hidden rounded-3xl">
              <img
                src={IMG.privateHero}
                alt="The branded Sita table set with gold lamps and plated desserts"
                data-testid="private-events-hero-photo"
                className="aspect-[4/5] w-full object-cover md:aspect-[5/5]"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ---------- Three offerings ---------- */}
      <section className="border-t border-forest/10">
        {OFFERINGS.map((o, i) => (
          <div
            key={o.title}
            data-testid={o.testid}
            className={`mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24 ${i > 0 ? "border-t border-forest/10" : ""}`}
          >
            <div className="grid items-center gap-12 lg:grid-cols-12">
              <FadeUp className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="overflow-hidden rounded-3xl">
                  <img src={o.img} alt={o.title} className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.02]" />
                </div>
              </FadeUp>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1 lg:pr-8" : "lg:pl-8"}`}>
                <FadeUp delay={0.1}>
                  <div className="flex items-center gap-4">
                    <img src={o.leaf} alt="" aria-hidden="true" className="h-14 w-14 object-contain" />
                    <span className="font-display text-sm italic text-forest/50">{o.n}</span>
                  </div>
                  <h2 className="mt-6 font-display text-4xl font-light text-forest md:text-5xl">{o.title}</h2>
                  <p className="mt-6 max-w-xl leading-relaxed text-ink/85">{o.copy}</p>
                  <div className="mt-8">
                    <TerraButton to={o.testid === "private-events-meal-prep" ? "/meal-prep" : "/inquiries"} testid={`${o.testid}-inquire-btn`}>
                      {o.testid === "private-events-meal-prep" ? (
                        <>See this week's menu <span aria-hidden="true">→</span></>
                      ) : (
                        <>Inquire <span aria-hidden="true">→</span></>
                      )}
                    </TerraButton>
                  </div>
                </FadeUp>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* ---------- The table is open ---------- */}
      <section data-testid="private-events-hosting" className="border-t border-forest/10 bg-cream">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <FadeUp className="lg:col-span-6">
              <div className="overflow-hidden rounded-3xl">
                <img
                  src={IMG.privateHosting}
                  alt="Dumplings plated by candlelight at a hosted night"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.02]"
                />
              </div>
            </FadeUp>
            <div className="lg:col-span-6 lg:pl-8">
              <FadeUp>
                <Overline>Hosting</Overline>
                <h2 className="mt-4 font-display text-4xl font-light text-forest md:text-5xl">The table is open</h2>
              </FadeUp>
              <FadeUp delay={0.1}>
                <div className="mt-8 space-y-5 leading-relaxed text-ink/85">
                  <p>
                    Sita was never only meant to be our table. It is a room that holds people, and it can hold yours too.
                  </p>
                  <p>Come cook as a guest chef, and the night is yours.</p>
                  <p>
                    Or let us do the cooking, so you can gather the people closest to you, enjoy a great night, and never
                    have to cook.
                  </p>
                  <p>A women's table, an LGBTQIA celebration, a birthday that deserves more than a restaurant.</p>
                  <p>Whatever you are gathering for, we bring the space, the food, and the care.</p>
                  <p className="font-display text-2xl italic text-forest">Supper clubs are for everyone.</p>
                </div>
                <div className="mt-10">
                  <TerraButton to="/inquiries" testid="private-events-hosting-inquire-btn">
                    Inquire about hosting <span aria-hidden="true">→</span>
                  </TerraButton>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- How booking works ---------- */}
      <section data-testid="private-events-booking" className="bg-deepforest text-cream">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <FadeUp>
                <Overline light>How booking works</Overline>
                <p className="mt-6 text-lg leading-relaxed text-cream/85">
                  You reach out. We talk through your night or your week. We send a custom proposal and a quote. A{" "}
                  {SITE.depositAmount} deposit holds your date, and we take it from there.
                </p>
              </FadeUp>
            </div>
            <FadeUp delay={0.15} className="lg:col-span-5 lg:pl-8">
              <p className="mb-4 text-sm leading-relaxed text-cream/60">
                Date already confirmed with us? Hold it right now:
              </p>
              <PayDepositButton />
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
